import type { IdentityCardType } from './dto-verification'
import type { PaymentPlan } from './PaymentPlan'
import type { RestResponseKey } from './shared-enums'

export interface CountryCodebookDTO {
    id: number
    name: string
    isoAlpha2Code: string
    isoAlpha3Code: string
    featured: boolean
}

export interface NewPasswordRequest {
    key: string
    newPassword: string
}

export interface PreQualifyQrCodeResponse {
    qrCodeBase64: string
    url: string
}

export interface Localized {
    id: number
    strings: Record<string, string>
}

export interface SecurityQuestion {
    id: number
    question: Localized
}

export interface VendorUserPasswordChangeRequestDTO {
    personId: number
    password: string
    passwordRepeat: string
    version?: number
}

export interface SecurityInfoDTO {
    phoneNumber: string
    phoneNumberVerified: boolean
    email: string
    emailVerified: boolean
    securityQuestions: SecurityQuestion[]
    currentSecurityQuestion: string
    currentSecurityQuestionId: number
}

export interface SecurityQuestionDTO {
    id: number
    question: string
}

export interface VendorSecurityInfoDTO {
    email: string
    emailVerified: boolean
    currentSecurityQuestion: string
    currentSecurityQuestionId: number
}

export interface SecurityQuestionChangeRequest {
    securityQuestionId: number
    answer: string
}

export interface ChangeApiUrlRequest {
    currentPassword: string
    newApiUrl: string
}

export interface AccountActivateRequest {
    key: string | null
    code?: string
}

export interface ChangeEmailRequest {
    emailNew: string
}

export interface ChangePasswordRequest {
    currentPassword: string
    newPassword: string
}

export interface ChangeSecretWordRequest {
    currentPassword: string
    newSecretWord: string
}

export enum WorkingDayOfWeek {
    MONDAY = 'MONDAY',
    TUESDAY = 'TUESDAY',
    WEDNESDAY = 'WEDNESDAY',
    THURSDAY = 'THURSDAY',
    FRIDAY = 'FRIDAY',
}

export interface VendorContactInfoDTO {
    name: string
    nameChanged: string | null
    description: string
    descriptionChanged: string | null
    logoUrl: string
    logoUrlChanged: string | null
    siteUrl: string
    siteUrlChanged: string | null
    address: string
    addressChanged: string | null
    companyNumber: string
    companyNumberChanged: string | null
    vatNumber: string
    vatNumberChanged: string | null
    accountNumber: string
    accountNumberChanged: string | null
    contactPersonName: string
    contactPersonNameChanged: string | null
    contactPersonTitle: string
    contactPersonTitleChanged: string | null
    contactPersonPhone: string
    contactPersonPhoneChanged: string | null
    contactPersonEmail: string
    contactPersonEmailChanged: string | null
    notificationEmail: string
    notificationEmailChanged: string | null
    settlementDay: WorkingDayOfWeek
    settlementDayChanged: WorkingDayOfWeek | null
}

export interface VendorDevelopmentInfoDTO {
    vendorApiKey: string
    vendorApiUrl: string
    vendorApiSecretSet: boolean
    preQualifiedUrl: string
}

export interface VendorDataDTO {
    contactInfo: VendorContactInfoDTO
    securityInfo: VendorSecurityInfoDTO
    developmentInfo: VendorDevelopmentInfoDTO
}

export interface VendorContactChangeRequest {
    name: string
    description: string
    logoUrl: string
    siteUrl: string
    address: string
    companyNumber: string
    vatNumber: string
    accountNumber: string
    contactPersonName: string
    contactPersonTitle: string
    contactPersonPhone: string
    contactPersonEmail: string
    notificationEmail: string
    settlementDay: WorkingDayOfWeek
}

export interface PasswordResetRequest {
    email: string
}

export enum DisputeType {
    CANCEL_CREDIT = 'CANCEL_CREDIT',
    ADVANCED_PAYMENT = 'ADVANCED_PAYMENT',
}
export enum VendorAccountStatus {
    ACTIVE = 'ACTIVE',
    DISABLED = 'DISABLED',
}
export interface VendorUserRoleDTO {
    roleId: number
    roleName?: string
}

export interface VendorUserRequestDTO {
    email: string
    firstName: string
    lastName: string
    hasAccessToVerificationApp: boolean
    roleId: number
    status: VendorAccountStatus
    version?: number
}

export interface VendorUserResponseDTO {
    id: number
    email: string
    firstName: string
    lastName: string
    role?: VendorUserRoleDTO
    hasAccessToVerificationApp: boolean
    status: VendorAccountStatus
    version: number
}

export interface TransactionDeliveryRequest {
    id: number
}

export interface TransactionPartialDeliveryRequest {
    id: number
    amount: number
}

export interface TransactionPartialDeliveryResponse {
    success: boolean
}

export interface DisputeRequest {
    transactionId: number
    message: string
    type?: DisputeType
    annexDocumentId?: number
    forceDispute?: boolean
}
export interface NationalityCodebookDTO {
    country: string
    name: string
    id: number
    nameEn: string
}
export interface IndustryOfEmploymentDTO {
    name: string
    id: number
    nameEn: string
}

export interface SubmitAdditionalDocumentValidationRequest {
    token: string
    documentType: string
    documentId: string
    firstName: string
    lastName: string
}

export interface StreetCodebookDTO {
    id: number
    code: string
    name: string
}
export interface PostZipCodebookDTO {
    id: number
    code: string
    displayName: string
    name?: string
}

export interface Page<T> {
    content: T[]
    totalElements: number
    totalPages: number
}

export interface SettlementCodebookDTO {
    id: number
    code: string
    name: string
}

export interface PostZipCodebookDTO {
    id: number
    code: string
    displayName: string
    name?: string
}

export interface PersonalDetailsCheckRequest {
    token: string
    taxNumber: string
    email: string
    couponCode?: string
    firstName: string
    lastName: string
    marketingConsent: boolean
    bankAccountNumber?: string
}

export interface PersonalDetailsV2Request {
    token: string
    firstName: string
    lastName: string
    email: string
    birthFirstName?: string
    birthLastName?: string
    mothersMaidenFirstName?: string
    mothersMaidenLastName?: string
    marketingConsent?: boolean
    placeOfBirth?: string
    gender?: string
    domesticCitizenshipAccepted?: boolean
    birthDate?: string
    countryOfBirthCodebookId?: number
    nationalityCodebookId?: number
    identityCardType?: string
    identityCardNumber?: string
    taxNumber?: string
    responsibilityAccepted?: boolean
    creditBureauConsentAccepted?: boolean
}

export interface FinancialProductV2Request {
    token: string
    maturityPriceId: any
    maturityGraceId: any
    firstPaymentDate: string
    insuranceIncluded?: boolean
}
export interface FinancialAccountNumberV2Request {
    token: string
    bankAccountNumber: string
}

export interface IbanDataRequest {
    token: string
    iban: string
    ibanConsent: boolean
}

export interface IbanDataResponse {
    token: string
    bankAccountNumber: string | null
    verificationStatus: string
}

export enum CheckoutStep {
    LOADING_SCREEN = 'LOADING_SCREEN',
    SCORING_DECISION = 'SCORING_DECISION',
    CREDIT_INSURANCE_INFO = 'CREDIT_INSURANCE_INFO',
    CANCEL_TRANSACTION = 'CANCEL_TRANSACTION',
    WATCH_LIST = 'WATCH_LIST',
    TERM = 'TERM',
    PRE_QUALIFIED_INFO = 'PRE_QUALIFIED_INFO',
    COOKIE_CONSENT = 'COOKIE_CONSENT',
    LOGIN_PHONE_NUMBER = 'LOGIN_PHONE_NUMBER',
    LOGIN_AUTHENTIFICATION = 'LOGIN_AUTHENTIFICATION',
    PERSONAL_DETAILS = 'PERSONAL_DETAILS',
    ADDITIONAL_DATA = 'ADDITIONAL_DATA',
    ADDRESS_DATA = 'ADDRESS_DATA',
    PSD2 = 'PSD2',
    FINANCIAL_DETAILS = 'FINANCIAL_DETAILS',
    FINANCIAL_DETAILS_OVERVIEW = 'FINANCIAL_DETAILS_OVERVIEW',
    VALIDATE_OFFER = 'VALIDATE_OFFER',
    ADJUST_OFFER = 'ADJUST_OFFER',
    APPROVED = 'APPROVED',
    CONTRACT = 'CONTRACT',
    PAYMENT = 'PAYMENT',
    TRANSACTION_PENDING = 'TRANSACTION_PENDING',
    TRANSACTION_SUCCESS = 'TRANSACTION_SUCCESS',
    TRANSACTION_ERROR = 'TRANSACTION_ERROR',
    PRE_QUALIFIED_SUCCESS = 'PRE_QUALIFIED_SUCCESS',
    CHECKOUT_SUCCESS = 'CHECKOUT_SUCCESS',
    PRE_QUALIFIED_ERROR = 'PRE_QUALIFIED_ERROR',
    CALL_ME = 'CALL_ME',
    CHECKOUT_ERROR = 'CHECKOUT_ERROR',
    TWO_FA_PIN = 'TWO_FA_PIN',
    FORGOT_TWO_FA_PIN = 'FORGOT_TWO_FA_PIN',
    KYC_VERIFICATION_INFO = 'KYC_VERIFICATION_INFO',
    KYC_VERIFICATION = 'KYC_VERIFICATION',
    KYC_VERIFICATION_POA_INFO = 'KYC_VERIFICATION_POA_INFO',
    KYC_VERIFICATION_POA = 'KYC_VERIFICATION_POA',
    SELF_CHECKOUT_INFO = 'SELF_CHECKOUT_INFO',
    ONBOARDING = 'ONBOARDING',
    ONBOARDING_SHORT = 'ONBOARDING_SHORT',
    KYC_FAILED = 'KYC_FAILED',
    SET_PURCHASE_AMOUNT = 'SET_PURCHASE_AMOUNT',
    BARCODE = 'BARCODE',
    INSURANCE_CONTRACT = 'INSURANCE_CONTRACT',
    ENTER_IBAN = 'ENTER_IBAN',
    CONSENTS = 'CONSENTS',
    FINANCIAL_DATA_CHECKING = 'FINANCIAL_DATA_CHECKING',
    OFFER_PLAN_DATA = 'OFFER_PLAN_DATA',
    CREDIT_ERROR = 'CREDIT_ERROR',
    CREDIT_REJECTION = 'CREDIT_REJECTION',
    PURCHASE_LIMIT = 'PURCHASE_LIMIT',
}

export enum CheckoutStepViewType {
    OPEN = 'OPEN',
    CLOSE = 'CLOSE',
}

export interface RecordStepParams {
    token: string
    // checkoutStepPriority?: number
    checkoutStep: CheckoutStep
    checkoutStepViewType: CheckoutStepViewType
    data: any
}

export interface CheckoutStepRecordRequest {
    token: string
    checkoutStepPriority?: number
    checkoutStep: CheckoutStep
    checkoutStepViewType: CheckoutStepViewType
    data?: string
}

export enum Gender {
    Male = 'Male',
    Female = 'Female',
}

export interface PersonalDetailsFormData {
    firstName: string
    lastName: string
    postZipId: number
    settlementId: number
    streetId?: number
    street?: string
    houseNumber: string
    gender: Gender
    birthDate: string
    placeOfBirth: string
    countryOfBirthCodebookId: number
    taxNumber: string
    email: string
    termsAccepted: boolean
    citizenshipAccepted: boolean
    coupon?: string
    bankAccountNumber?: string
    marketingConsent?: boolean
    termsPart1: boolean
    termsPart2: boolean
}

export interface Testimonial {
    personName: string
    text: string
}

export interface PersonalDetailsData {
    firstName: string
    lastName: string
    countryOfBirth: string
    email: string
    street: string
    birthDate: string
    houseNumber: string
    settlement: number
    postZip: string
    countryOfBirthCodebookId: number
    placeOfBirth: string
    gender: string
    taxNumber: ''
    coupon?: string
    citizenship: boolean
    termsPart1: boolean
    termsPart2: boolean
    marketingConsent: boolean
    bankAccountNumber?: string
}

export interface PersonalDetailsDTO {
    firstName: string | null
    birthFirstName: string | null
    lastName: string | null
    birthLastName: string | null
    birthDate: string | null
    email: string | null
    postZipCodebookId: number | null
    settlementCodebookId: number | null
    streetCodebookId: number | null
    street: string | null
    houseNumber: string | null
    marketingConsent: boolean
    bankAccountNumber: string | null
    motherMaidenFirstName: string | null
    motherMaidenLastName: string | null
    countryOfBirthCodebookId: number | null
    nationalityCodebookId: number | null
    placeOfBirth: string | null
    additionalAddressDetails: string | null
    identityCardType: IDDocumentType | null
    identityCardNumber: string | null
    taxNumber?: string | null
}

export interface PersonalDetailsDTOV2 {
    firstName: string
    lastName: string
    email: string
    marketingConsent: boolean
    birthFirstName: string
    birthLastName: string
    mothersMaidenFirstName: string
    mothersMaidenLastName: string
    placeOfBirth: string
    gender: Gender
    domesticCitizenshipAccepted?: boolean
    birthDate?: string
    countryOfBirthCodebookId: number
    identityCardType: IDDocumentType
    identityCardNumber: string
    taxNumber: string
    responsibilityAccepted: boolean
    creditBureauConsentAccepted: boolean
    nationalityCodebookId?: number
}

export interface AccountSingleStepPinRequest {
    phoneNumber: string
    token?: string
}

export interface JWTLoginRequest {
    token?: string
    username: string
    password: string
    rememberMe?: boolean
}

export interface AuthResponse {
    idToken: string
}
export interface AuthRequest {
    username: string
    password: string
}

// Single-use handoff between the checkout and credit apps: checkout issues a code bound
// to its own client session, credit exchanges it once for a session of its own.
export interface HandoffIssueRequest {
    vendorToken: string
}

export interface HandoffIssueResponse {
    handoffCode: string
    expiresAt: string
    // Server-built and used verbatim — the FE never assembles the credit app URL.
    redirectUrl: string
}

export interface HandoffExchangeRequest {
    handoffCode: string
}

export interface HandoffExchangeResponse {
    idToken: string
    vendorToken: string
}

export enum FinancialDetailsOptionsTypes {
    TYPE_1 = 'TYPE_1',
    TYPE_2 = 'TYPE_2',
}

export enum EmploymentType {
    FULL_TIME = 'FULL_TIME',
    PART_TIME = 'PART_TIME',
    SP_IT = 'SP_IT',
    SP_OTHER = 'SP_OTHER',
    STUDENT = 'STUDENT',
    RETIRED = 'RETIRED',
    UNEMPLOYED = 'UNEMPLOYED',
    OTHER = 'OTHER',
    FARMER = 'FARMER',
    FREE_ARTIST = 'FREE_ARTIST',
    SEASONAL_WORKER = 'SEASONAL_WORKER',
}

export enum EmploymentContractType {
    DO_NOT_WANT_TO_ASK = 'DO_NOT_WANT_TO_ASK',
    PERMANENT = 'PERMANENT',
    TEMPORARY = 'TEMPORARY',
    HONORARY = 'HONORARY',
}

export interface EmploymentMetaTypeDTO {
    name: EmploymentType
    contractTypes: EmploymentContractType[]
}

export enum ExecutionAmountDTO {
    LOWER_THAN_100 = 'LOWER_THAN_100',
    BETWEEN_101_200 = 'BETWEEN_101_200',
    GREATER_THAN_200 = 'GREATER_THAN_200',
}

export enum LatePaymentTime {
    BETWEEN_1_AND_7_DAYS = 'BETWEEN_1_AND_7_DAYS',
    BETWEEN_8_AND_15_DAYS = 'BETWEEN_8_AND_15_DAYS',
    BETWEEN_16_AND_30_DAYS = 'BETWEEN_16_AND_30_DAYS',
    GREATER_THAN_30_DAYS = 'GREATER_THAN_30_DAYS',
}

export interface ExecutionAmountMetaDTO {
    id: number
    orderNumber: number
    fromAmount: number
    toAmount: number
    name: string
}

export interface FinancialDetailsRequestV2 {
    token: string
    income?: number
    employmentType?: EmploymentType
    monthlyCreditExpense?: number
    monthlyHouseCredit?: number
    monthlyCreditExpenseInLastThreeMonthExcludeLoan?: number
    monthlyCreditExpenseExcludeLoan?: number
    monthlyCashOrMortgageCredit?: number
    monthlyLeasingOrAutoCredit?: number
    employerCredit?: boolean
    useCreditCardForInstallmentPayment?: boolean
    creditCardLimit?: number
    executionInLastYear?: boolean
    executionInLastYearAmountId?: number
    executionInLastYearCreditOrLeasing?: boolean
    personalBankruptcyInProgress?: boolean
    lateInstallmentPayment?: boolean
    lateInstallmentPaymentTime?: LatePaymentTime | null
    employmentContractType?: EmploymentContractType
    numberOfDependants?: number
    numberOfChildren?: number
    numberOfConsecutivePaymentMonths?: string
    financialProductBankAccountLimit?: boolean
    financialProductCreditCard?: boolean
    financialProductBankCredit?: boolean
    financialProductLeasing?: boolean
    industryCodebookId?: number
    historicalLoanLessThanAmount?: boolean
    bankAccountNumber?: string
    nrLoansInLastYear?: string
    nrLoansDelaysInLastYear?: string
}

export interface FinancialDetailsCheckRequest {
    token: string
    financialProductBankAccountLimit?: boolean
    financialProductCreditCard?: boolean
    financialProductBankCredit?: boolean
    financialProductLeasing?: boolean
    income: number
    monthlyCreditExpense?: number
    monthlyHouseCredit?: number
    monthlyCashOrMortgageCredit?: number
    monthlyLeasingOrAutoCredit?: number
    employerCredit?: boolean
    useCreditCardForInstallmentPayment?: boolean
    creditCardLimit?: number
    executionInLastYear: boolean
    executionInLastYearCreditOrLeasing?: boolean
    executionInLastYearAmount?: ExecutionAmountDTO
    personalBankruptcyInProgress: boolean
    lateInstallmentPayment?: boolean
    lateInstallmentPaymentTime?: LatePaymentTime
    employmentType: EmploymentType
    employmentContractType?: EmploymentContractType
    householdMembersNumber: number
    bankAccountNumber?: string
    numberOfChildren?: number
    numberOfConsecutivePaymentMonths?: string
    numberOfRecentLoans?: string
}

export interface FinancialDetailsValidationDataDTO {
    status: boolean
    errorKeys: RestResponseKey[]
}

export interface PreQualifiedRequest {
    surrogateId: string | null
    token: string
}

export interface LoanAmountLimitsRequest {
    vendorId: string
}

export interface TransactionRequest {
    surrogateId: string | null
    token: string
    maturityPriceId: number
    maturityGraceId: number
    firstPaymentDate: number

    // ----------- <FINANCIAL DETAILS> -------------------------------------------
    checkoutFlowType?: string
    income?: number
    employmentType?: string
    bankAccountNumber?: string
    monthlyCreditExpense?: number
    monthlyHouseCredit?: number
    monthlyCashOrMortgageCredit?: number
    monthlyLeasingOrAutoCredit?: number
    employerCredit?: boolean
    useCreditCardForInstallmentPayment?: boolean
    creditCardLimit?: number
    executionInLastYear?: boolean | null
    executionInLastYearCreditOrLeasing?: boolean | null
    executionInLastYearAmount?: ExecutionAmountDTO | null
    personalBankruptcyInProgress?: boolean | null
    lateInstallmentPayment?: boolean | null
    lateInstallmentPaymentTime?: LatePaymentTime | null
    employmentContractType?: EmploymentContractType | null
    householdMembersNumber?: number
    numberOfDependants?: number | string
    numberOfChildren?: number
    numberOfConsecutivePaymentMonths?: string
    numberOfRecentLoans?: string
}

export interface ApplicationPreQualifiedRequest extends PreQualifiedRequest {
    firstName: string
    lastName: string
    email?: string
    zip?: string
    city?: string
    postZipCodebookId: number
    settlementCodebookId: number
    streetCodebookId?: number
    street?: string
    houseNumber: string | number
    gender: Gender
    birthDate: string
    placeOfBirth: string
    countryOfBirth?: string
    countryOfBirthCodebookId: number
    taxNumber: string
    responsibilityAccepted: boolean
    citizenshipOfSloveniaAccepted: boolean
    financialProductBankAccountLimit?: boolean
    financialProductCreditCard?: boolean
    financialProductBankCredit?: boolean
    financialProductLeasing?: boolean
    useCreditCardForInstallmentPayment?: boolean
    creditCardLimit?: number
    executionInLastYear?: boolean | null
    executionInLastYearCreditOrLeasing?: boolean | null
    executionInLastYearAmount?: ExecutionAmountDTO | null
    personalBankruptcyInProgress?: boolean | null
    lateInstallmentPayment?: boolean | null
    lateInstallmentPaymentTime?: LatePaymentTime | null
    monthlyCreditExpense?: number
    monthlyHouseCredit?: number
    monthlyCashOrMortgageCredit?: number
    monthlyLeasingOrAutoCredit?: number
    employerCreditYesNo?: boolean
    income?: number
    employmentType?: EmploymentType
    employmentContractType?: EmploymentContractType
    numberOfDependants?: number | string
    numberOfChildren?: number
    bankAccountNumber?: string
    bankAccountNumberUsedForDirectDebit?: boolean | null
    numberOfConsecutivePaymentMonths?: string
    numberOfRecentLoans?: string
    checkoutFlowType?: string
}

export interface ApplicationRequest extends Partial<TransactionRequest> {
    firstName: string
    lastName: string
    email?: string
    zip?: string
    city?: string
    postZipCodebookId: number
    settlementCodebookId: number
    streetCodebookId?: number
    street?: string
    houseNumber: string | number
    gender: Gender
    birthDate: string
    placeOfBirth: string
    countryOfBirth?: string
    countryOfBirthCodebookId: number
    taxNumber: string
    responsibilityAccepted: boolean
    citizenshipOfSloveniaAccepted: boolean
    financialProductBankAccountLimit?: boolean
    financialProductCreditCard?: boolean
    financialProductBankCredit?: boolean
    financialProductLeasing?: boolean
    useCreditCardForInstallmentPayment?: boolean
    creditCardLimit?: number
    executionInLastYear?: boolean | null
    executionInLastYearCreditOrLeasing?: boolean | null
    executionInLastYearAmount?: ExecutionAmountDTO | null
    personalBankruptcyInProgress?: boolean | null
    lateInstallmentPayment?: boolean | null
    lateInstallmentPaymentTime?: LatePaymentTime | null
    monthlyCreditExpense?: number
    monthlyHouseCredit?: number
    monthlyCashOrMortgageCredit?: number
    monthlyLeasingOrAutoCredit?: number
    employerCreditYesNo?: boolean
    income?: number
    employmentType?: EmploymentType
    employmentContractType?: EmploymentContractType
    numberOfDependants?: number | string
    numberOfChildren?: number
    bankAccountNumber?: string
    bankAccountNumberUsedForDirectDebit?: boolean | null
    numberOfConsecutivePaymentMonths?: string
    numberOfRecentLoans?: string
    checkoutFlowType?: string
}

export interface ApplicationRequestROM extends Omit<TransactionRequest, 'surrogateId'> {
    firstName: string
    lastName: string
    postZipCodebookId: number
    houseNumber: string
    taxNumber: string
    responsibilityAccepted: boolean
    employmentType?: EmploymentType
    income: number
    email: string
    placeOfBirth: string
    countryOfBirthCodebookId: number
    settlementCodebookId: number
    birthDate: string
    street: string
    additionalAddressDetails: string
}

export interface MaturityDTO {
    id: number
    numberOfMonths: number
    description: string
}

export interface PaymentPlanDTO {
    possible: boolean
    termMaturity: MaturityDTO
    graceMaturity: MaturityDTO
    loanAmount: number
    totalAmount: number
    totalAmountWithoutDiscount: number
    graceAmount: number
    installmentAmount: number
    firstInstallmentAmount: number
    firstInstallmentDate: number
    numberOfInstallments: number
    nominalInterestRate: number
    /** null when the plan carries no discount */
    nominalInterestRateWithoutDiscount: number | null
    costOfReview: number
    costOfRisk: number
    requiresCreditReviewFeeConsent: boolean | null
    clientInterest: number
    clientInterestWithoutDiscount: number
    interestAndFees: number
    interestAndFeesWithoutDiscount: number
    eom: number
    /** null when the plan carries no discount */
    eomWithoutDiscount: number | null
    downPaymentTotal: number
    downPaymentPrincipal: number
    calculationMethod: CostsCalculationMethod
    totalLoanAmount: number
    insurancePremiumFee: number
}

export enum CostsCalculationMethod {
    FIRST_INSTALLMENT = 'FIRST_INSTALLMENT',
    DIVIDED_ON_INSTALLMENTS = 'DIVIDED_ON_INSTALLMENTS',
}

export interface PaymentDataOption {
    numberOfMonths: number
    paymentPlan: PaymentPlan
    possible: boolean
}

export enum TestAB {
    A = 'A',
    B = 'B',
}

export interface ValidationDataDTO {
    applicationId: number
    status: boolean
    validatedPaymentPlan: PaymentPlanDTO
    hasPossibleCashFlows: boolean
    paymentPlans: PaymentPlanDTO[]
    availableExposure: number
    availableExposureCpiIncluded: number
    recommendedPurchaseAmount: number
    testGroup: TestAB
    failedReasonType?: 'EMPTY' | 'NO_PAYMENT_OPTIONS' | 'FAIL_CREDIT_CHECK'
    shadowLimit?: number
    purchaseAmount?: PurchaseAmountDTO
}
export interface OfferValidationResultDTO {
    applicationId: number
    loanAmount: number
    insurancePremium: number
    eom: number
    firstPaymentDate: number[]
    installmentAmount: number
    interestsAndFees: number
    nominalInterestRate: number
    terms: number
    totalLoanAmount: number
    total: number
    // Served only where the validation step persists them — the backend omits absent fields rather
    // than nulling them, so markets that do not calculate a risk cost see an unchanged payload.
    costOfRisk?: number
    calculationMethod?: CostsCalculationMethod
}

export interface ValidationDataV2DTO {
    token: string
}

export interface VendorApiInitResponse {
    token: string
    vendorRequestType: string
    inStoreFlag: boolean
    vendorPackageDescription: string
    customerLanguage: string
    vendorName: string
    graceEnabled: boolean
    insuranceRatePercent?: number
    rbpEnabled?: boolean
    upsellEnabled?: boolean
    // true when the request is in-store AND the latest PP + T&C consents are
    // already accepted (captured in-store via F9 / TEC-19045); false otherwise.
    latestConsentsAccepted?: boolean
}

export interface ApplicationScoringRequest {
    applicationId: number
    token: string
}

export interface ValidateOfferInitialResponse {
    applicationId: number
    token: string
}

export enum ScoringStatus {
    CREATED = 'CREATED',
    PENDING = 'PENDING',
    DONE = 'DONE',
    FAILED = 'FAILED',
}

export interface ApplicationScoringResponse {
    applicationId: number
    status: ScoringStatus
}

export interface ValidationDataResultRequest {
    applicationId: number
    maturityPriceId?: number
    maturityGraceId?: number
    token: string
}

// Same wire strings the FE already receives in the validate-offer 400 error header, so they resolve
// through the existing `errorMessages.<key>` translation lookup.
export type ValidateOfferDeclineReason =
    | 'PERSON_CITIZENSHIP_CONSTRAINT_OCCURRED'
    | 'PERSON_MAX_AGE_CONSTRAINT_OCCURRED'
    | 'PERSON_MAX_AGE_AT_LOAN_MATURITY_CONSTRAINT_OCCURRED'
    | 'PERSON_MIN_AGE_CONSTRAINT_OCCURRED'
    | 'PERSON_MIN_INCOME_CONSTRAINT_OCCURRED'
    | 'PERSON_MIN_VERIFIED_INCOME_CONSTRAINT_OCCURRED'
    | 'PERSON_MAX_EXPOSURE_CONSTRAINT_OCCURRED'
    | 'PERSON_MAX_ACTIVE_DUE_DAYS_ON_LP_CONSTRAINT_OCCURRED'
    | 'PERSON_MAX_MR_DIVIDED_BY_INCOME_CONSTRAINT_OCCURRED'
    | 'PERSON_PSD2_IBAN_35_CONSTRAINT_OCCURRED'
    | 'PERSON_CONCURRENT_APPLICATIONS_CONSTRAINT_OCCURRED'
    | 'PERSON_EXECUTION_OVER_SPECIFIC_AMOUNT_CONSTRAINT_OCCURRED'
    | 'PERSON_LATE_INSTALLMENT_OVER_SPECIFIC_DAYS_CONSTRAINT_OCCURRED'
    | 'PERSON_KNOCKED_OUT'
    | 'PERSON_IBAN_35_PROTECTED_CONSTRAINT_OCCURRED'
    | 'KYC_TIMEOUT_OCCURRED'
    | 'CREDIT_BUREAU_CONSENT_OCCURRED'
    | 'KYC_SANCTION'
    | 'CHECK_AIS_ENFORCEMENT_CONSTRAINT_OCCURRED'
    | 'ELIGIBLE_DTI_OCCURRED'
    | 'CHECK_DOCUMENT_VALIDATION_TIMEOUT'
    | 'CHECK_DOCUMENT_VALIDITY'

export interface ValidateOfferResultResponse {
    reasonKey: ValidateOfferDeclineReason
    // Technical English rule label for debugging. Not localized — must never reach the UI.
    description: string
}

export interface GetSelfCheckoutTokenRequest {
    vendorId: string
    financialProductCode?: string
    amount: number
}

export enum ConsentType {
    KYC_MOTION = 'KYC_MOTION',
}

export interface AcceptConsentRequest {
    type: ConsentType
}

export interface CreatePinRequest {
    pin: string
    rePin: string
}

export enum MembershipStatus {
    NOT_SUBMITTED = 'NOT_SUBMITTED',
    PENDING = 'PENDING',
    CONTRACT_SENT = 'CONTRACT_SENT',
    CONTRACT_SUBMITTED = 'CONTRACT_SUBMITTED',
    CONFIRMED = 'CONFIRMED',
    REJECTED = 'REJECTED',
    RESTARTED = 'RESTARTED',
}

export enum AccountVerificationStatus {
    EXPIRED = 'EXPIRED',
    NOT_VERIFIED = 'NOT_VERIFIED',
    PENDING = 'PENDING',
    VERIFIED = 'VERIFIED',
}

export enum TwoFAStatus {
    SET = 'SET',
    NOT_SET = 'NOT_SET',
    LOCKED = 'LOCKED',
    DISABLED = 'DISABLED',
}

export interface SessionInfoDTO {
    firstName: string | null
    lastName: string | null
    membershipStatus: MembershipStatus
    accountVerificationStatus: AccountVerificationStatus
    creditRating: string
    emailVerified: boolean | null
    annexActive: boolean
    pendingApproval: boolean
    hasPreApprovedTransaction: boolean
    customOfferUrlEnabled: boolean
    remoteOfferEnabled: boolean | null
    loginActions: string[] | null
    twoFAStatus: TwoFAStatus
    cardPaymentEnabled: boolean
    userIdentityNumber: string | null
    email: string | null
    checkoutFlowType: CheckoutFlowType
    preQualifiedSuccessCount: number
    progressBarType: 'SIMPLE' | 'ENHANCED'
    inStoreVerificationEnabled?: boolean
}
export interface ChangePinRequest {
    currentPin: string
    newPin: string
    newRePin: string
}

export interface ReCreatePinRequest {
    pin: string
    rePin: string
    last3DigitsOfTaxNumber?: string
    last3DigitsOfBankAccountNumber?: string
    mothersMaidenLastName?: string
}

export interface ProfileIbanChangeRequest {
    bankAccountNumber: string
}

export interface PersonMarketingConsentChangeRequest {
    consentMarketing: boolean
}

export interface ProfileAddressChangeRequest {
    postZipCodebookId: number
    settlementCodebookId: number
    streetCodebookId?: number
    street?: string
    houseNumber: string
}

export interface PreQualifiedInfoRequest {
    token: string
}

export interface PreQualifiedInfoDTO {
    vendorKnown: boolean
    vendorName: string
    returnUrl: string
    incomeLimit: number
}
export interface VendorApiPreQualifiedRequest {
    vendor?: string
    returnUrl?: string
    requestType?: VendorRequestType
    productCode?: string
}

export interface SystemConfigResponse {
    psd2SandboxModeEnabled: boolean
}

export interface Psd2TokenRequest {
    token: string
    data?: string
    bankId?: number
    iban?: string
    language?: string
    returnTo?: string
    externalConnectionId?: string
}

export enum BankszamlakivonatCode {
    SUCCESS = 'SUCCESS',
    ACCOUNT_NOT_ATTACHED = 'ACCOUNT_NOT_ATTACHED',
    ACCOUNT_ATTACHMENT_FAILED = 'ACCOUNT_ATTACHMENT_FAILED',
}

export interface ConnectionStatusRequest {
    customerId: string
    connectionId: string
    status: BankszamlakivonatCode
}

export interface Psd2TokenRequestResponse {
    redirectUrl: string
}

export interface Psd2ConnectionStatusRequest {
    token: string
    connectionId: string
}

export interface Psd2ConnectionStatusResponse {
    accessGranted: boolean
    data: string
    nameMatched: boolean
}

export interface PaymentDataDTO {
    purchaseAmount: number
    paymentCycleDates?: number[]
    gracePaymentCycleDates?: number[]
    successUrl: string
    errorUrl: string
    siteUrl: string
    paymentPlans: PaymentPlanDTO[]
    showCoupon: boolean
    pendingApproval: boolean
    pendingSisbonCheck: boolean
    incomeLimit: number
    maxGlobalTotalLoanAmountLimit: number
    maxGlobalTotalLoanAmountCpiIncludedLimit: number
    minGlobalTotalLoanAmountLimit: number
}

export interface CheckoutDataDTO {
    token: string
    sessionInfo?: SessionInfoDTO | null
    maturityPriceId?: number
    maturityGraceId?: number
    firstPaymentDate?: number
    authToken?: string
    personalDetails?: PersonalDetailsClientDTO | null
    paymentData?: PaymentDataDTO | null
    isVerified?: boolean
    isInStore?: boolean
    latestConsentsAccepted?: boolean
    paymentPlan?: PaymentPlanDTO | null
    incomeLimit?: number
    vendorKnown?: boolean
    vendorName?: string
    returnUrl?: string
    vendorRequestType?: string
    vendorPackageDescription?: string
    customerLanguage?: string
    pendingApproval?: boolean
    applicationApprovalType?: string
    graceEnabled?: boolean
    insuranceEnabled?: boolean
    insuranceRatePercent?: number
    isInsuranceEnabledSelected?: boolean
    rbpEnabled?: boolean
    upsellEnabled?: boolean
}

export interface CurrencyInfo {
    primaryCurrency: string
    secondaryCurrency: string | null
    multiCurrency: boolean
    conversionRate: number | null
}

export enum CurrencySymbols {
    EUR = '€',
    lei = 'lei',
    Ft = 'Ft',
}

export interface PaymentDataRequest {
    token: string
    firstPaymentDate?: number
    maturityType?: MaturityType
    insurancePlansOnly?: boolean
}

// SLO GPL (legacy MPL) payment-data request — POST api/mpl/v1/payment-data.
// firstPaymentDate is '' on first load and the selected cycle date (DDMMYYYY) on cycle change.
// The wire response uses string dates; checkoutService maps it to the existing PaymentData type.
export interface MplPaymentDataRequest {
    token: string
    amount: number
    firstPaymentDate: string
}

// SLO GPL (legacy MPL) post-sign final assessment — GET api/checkout/checkout-status.
// Mirrors legacy leanpay-client-new CheckoutRequestStatus + CheckoutStatusResponse.
export enum MplCheckoutRequestStatus {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    VALIDATION_IN_PROGRESS = 'VALIDATION_IN_PROGRESS',
}

export interface MplCheckoutStatusResponse {
    isProcessed: boolean
    status: MplCheckoutRequestStatus
    type: string
    additionalInfo: {
        applicationApprovalCheckType: string
        mplWorkingHours: boolean
        sisbonWorkingHours: boolean
    }
}

export enum MaturityType {
    GRACE = 'GRACE',
    PRICE = 'PRICE',
}

export interface PaymentDataDTO {
    purchaseAmount: number
    paymentCycleDates?: number[]
    successUrl: string
    errorUrl: string
    siteUrl: string
    paymentPlans: PaymentPlanDTO[]
    showCoupon: boolean
    pendingApproval: boolean
    pendingSisbonCheck: boolean
    incomeLimit: number
}

export interface TransactionRequest {
    surrogateId: string | null
    token: string
    maturityPriceId: number
    maturityGraceId: number
    firstPaymentDate: number
    income?: number
    employmentType?: string
}

export interface RequestFinancialDataRequest {
    token: string
    firstName: string
    lastName: string
    userIdentityNumber: string
}

export interface FinancialDataResponse {
    requestId: string
    userId: string
    userIdentityNumber: string
    income: number
    status: string
    employmentType: string
    additionalEmploymentInfoDataRequired?: boolean
}

export interface PersonBaseInfoDTO {
    firstName: string
    lastName: string
    phoneNumber: string
    email: string
    blocked: boolean
    verified: boolean
    accountVerificationStatus?: AccountVerificationStatus
    identityNumber?: string
}

// In-store KYC upload slots — keys must match BE KycDocumentType enum values exactly
export enum KycDocumentType {
    IDENTITY_CARD_FRONT_SIDE = 'IDENTITY_CARD_FRONT_SIDE',
    IDENTITY_CARD_BACK_SIDE = 'IDENTITY_CARD_BACK_SIDE',
    PROOF_OF_ADDRESS = 'PROOF_OF_ADDRESS',
    OTHER = 'OTHER',
}

export interface PersonVerifyRequest {
    phoneNumber: string
    identityNumber: string
    email: string
    firstName: string
    lastName: string
    identityCardType: IdentityCardType
    identityCardNumber: string
    identityCardDateOfExpire: string
    identityCardPlaceOfIssue: string
    // ISO date (yyyy-MM-dd); BE validates @PastOrPresent
    identityCardDateOfIssue: string
    countryOfBirthCodebookId: number
    placeOfBirth: string
    postZipCodebookId: number
    settlementCodebookId: number
    streetName: string
    houseNumber: string
    // F13 (TEC-19596): full address as printed on the Carte de Identitate; persisted to
    // Membership.identityCardAddress as typed (no BE-side concatenation).
    identityCardAddress: string
    customerIdentified: boolean
    // Raw base64 file bytes per slot, no data: prefix. Required slots: ID front + back.
    documents: Partial<Record<KycDocumentType, string>>
}

// RO in-store OTP consent flow (TEC-19045). Sent to leanpay-core /api/person.
export interface RequestPinRequest {
    phoneNumber: string
}

export interface AuthenticatePinRequest {
    phoneNumber: string
    pin: string
}

export type AdditionalEmploymentInfo = FinancialDataResponse

export enum FinancialDataStatus {
    'IN_PROCESSING' = 'IN_PROCESSING',
    'RESULTS_UNAVAILABLE' = 'RESULTS_UNAVAILABLE',
    'RESULTS_AVAILABLE' = 'RESULTS_AVAILABLE',
    'RESULTS_INVALID' = 'RESULTS_INVALID',
}

export interface ContractContentDTO {
    items: ContractSectionDTO[]
}

export enum DocumentType {
    REPRESENTATIVE_EXAMPLE = 'REPRESENTATIVE_EXAMPLE',
    DIGITAL_VERIFICATION_POLICY = 'DIGITAL_VERIFICATION_POLICY',
    TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
    CONSUMER_LOAN_LAW = 'CONSUMER_LOAN_LAW',
    PRIVACY_POLICY = 'PRIVACY_POLICY',
    PRICE_LIST = 'PRICE_LIST',
    INSURANCE_CONTRACT = 'INSURANCE_CONTRACT',
    INSURANCE_CONTRACT_PRELIMINARY = 'INSURANCE_CONTRACT_PRELIMINARY',
    CONTRACT = 'CONTRACT',
    CONTRACT_DRAFT = 'CONTRACT_DRAFT',
    CONTRACT_PRELIMINARY = 'CONTRACT_PRELIMINARY',
    VERIFICATION_DOCUMENT = 'VERIFICATION_DOCUMENT',
    CONTRACT_ANNEX = 'CONTRACT_ANNEX',
    INSTALLMENT_PLAN = 'INSTALLMENT_PLAN',
    INSTALLMENT_PLAN_DRAFT = 'INSTALLMENT_PLAN_DRAFT',
    SEPA_DIRECT_DEBIT = 'SEPA_DIRECT_DEBIT',
    SEPA_CONSENT = 'SEPA_CONSENT',
    PEP = 'PEP',
    INVOICE = 'INVOICE',
    SISBON_REPORT = 'SISBON_REPORT',
    COOKIE_POLICY = 'COOKIE_POLICY',
    COMPLAINT_POLICY = 'COMPLAINT_POLICY',
    FULL_ADVANCED_PAYMENT = 'FULL_ADVANCED_PAYMENT',
    TRADECORE_TERMS_AND_CONDITIONS = 'TRADECORE_TERMS_AND_CONDITIONS',
    MONAVATE_TERMS_AND_CONDITIONS = 'MONAVATE_TERMS_AND_CONDITIONS',
    FINANCIAL_DATA_AGREEMENT = 'FINANCIAL_DATA_AGREEMENT',
    CREDIT_BUREAU_AGREEMENT = 'CREDIT_BUREAU_AGREEMENT',
    HARDWARE_AND_SOFTWARE_COMPATIBILITY = 'HARDWARE_AND_SOFTWARE_COMPATIBILITY',
}

export interface ContractDataComplexDTO {
    documentType: DocumentType
    contractTitle: string
    contractHeader: ContractHeaderDTO | null
    contractContent: ContractContentDTO | null
    contractFooter: ContractFooterDTO | null
}

export interface ContractFooterDTO {
    items: ContractParagraphDTO[]
}

export interface ContractHeaderDTO {
    items: ContractSectionDTO[]
}

export interface ContractParagraphDTO {
    content: string
}

export interface ContractSectionDTO {
    caption: string
    items: ContractParagraphDTO[]
}

export enum RenderingType {
    FREE_TEXT = 'FREE_TEXT',
    BOOLEAN = 'BOOLEAN',
    SINGLE_CHOICE = 'SINGLE_CHOICE',
    MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
}
export interface QuestionDTO {
    id: number
    orderNumber: number
    localizationCode: string
    label: string
    value: string | null
    answerId: number
    renderingType: RenderingType
    answers: AnswerDTO[]
    groupName?: string
}

export interface QuestionnaireDTO {
    id: number
    title: string
    description: string
    questions: QuestionDTO[]
    conditionalShowing?: boolean
    additionalQuestions?: QuestionDTO[]
}

export interface LoanAmountLimitDTO {
    IN_STORE: {
        minAmount: number
        maxAmount: number
    }
    IN_STORE_SELF_CHECKOUT: {
        minAmount: number
        maxAmount: number
    }
    REMOTE: {
        minAmount: number
        maxAmount: number
    }
}
export interface QuestionnaireListDTO {
    basicQuestionnaire: QuestionnaireDTO
    additionalQuestionnaire?: QuestionnaireDTO
}

export interface VendorProductValidationRequest {
    vendor: string
    productCode?: string
    requestType: VendorRequestType
}

export interface AnswerDTO {
    id: number
    localizationCode: string
    label: string
    answerType: AnswerType
    value: string
}

export enum AnswerType {
    FREE_TEXT = 'FREE_TEXT',
    BOOLEAN = 'BOOLEAN',
}

export interface ContractPreliminaryRequest {
    surrogateId: string | null
    token: string
}

export interface QuestionResponseDTO {
    questionId: number
    answerId?: number | null
    value: string | null
    renderingType?: string
}

export interface PepQuestionnaireResponseDTO {
    questionnaireId: number
    applicationId: number
    questionResponses: QuestionResponseDTO[]
}

// Legacy SLO MPL questionnaire submit (POST api/checkout/questionnaire), sent before sign-or-buy.
export interface QuestionnaireRequest {
    vendorRequestToken: string
    pepQuestionnaireResponse: PepQuestionnaireResponseDTO
    additionalPepQuestionnaireResponse?: PepQuestionnaireResponseDTO
}

export interface ContractSignOrBuyRequest {
    surrogateId?: string
    token: string
    maturityPriceId: number
    maturityGraceId: number
    firstPaymentDate: number
    contractSigned: boolean
    sepaDirectDebitSigned?: boolean
    pepSigned: boolean
    pepQuestionnaireResponse?: PepQuestionnaireResponseDTO | null
    additionalPepQuestionnaireResponse?: PepQuestionnaireResponseDTO | null
}

export interface SignOrBuyResponse {
    status: SignOrBuyStatus
    applicationApprovalId: number
    applicationApprovalCheckType: ApplicationApprovalCheckType
}

export enum ApplicationApprovalCheckType {
    NO_APPROVAL = 'NO_APPROVAL',
    SISBON = 'SISBON',
    BASIC = 'BASIC',
    LIGHT = 'LIGHT',
    FULL = 'FULL',
    PEP = 'PEP',
    MEDIUM = 'MEDIUM',
    RISK = 'RISK',
    CB_POSITIVE_CHECK = 'CB_POSITIVE_CHECK',
    PENSIONER = 'PENSIONER',
    PUBLIC_SERVICE = 'PUBLIC_SERVICE',
    ADDRESS_PROOF = 'ADDRESS_PROOF',
    AML_THRESHOLD = 'AML_THRESHOLD',
}

export enum SignOrBuyStatus {
    PROCEED_TO_PAYMENT = 'PROCEED_TO_PAYMENT',
    TRANSACTION_CREATED = 'TRANSACTION_CREATED',
    PENDING_APPROVAL = 'PENDING_APPROVAL',
    REJECTED = 'REJECTED',
}

export interface StripeCardRequest {
    stripeToken: string
}

export interface StripeChargeRequest {
    amount: number
    creditCardId?: number
    stripeToken?: string
}

export interface StripeTestRequest {
    amount: number
    stripeToken: string
}

export enum CreditCardType {
    AMEX = 'AMEX',
    DINERS = 'DINERS',
    DISCOVER = 'DISCOVER',
    JCB = 'JCB',
    MASTER = 'MASTER',
    VISA = 'VISA',
    UNKNOWN = 'UNKNOWN',
}

export interface CreditCardDTO {
    id: number
    cardNumber: string
    expirationDate: string
    type: CreditCardType
}

export enum CardPaymentType {
    AUTHORIZATION = 'AUTHORIZATION',
    DOWN = 'DOWN',
    LATE = 'LATE',
    DISPUTE = 'DISPUTE',
    UPCOMING_OR_DUE = 'UPCOMING_OR_DUE',
}

export enum CardPaymentProvider {
    WIRECARD = 'WIRECARD',
    PAYDOO = 'PAYDOO',
    NETOPIA = 'NETOPIA',
}

export enum CardPaymentStatus {
    INITIALIZED = 'INITIALIZED',
    CREATED = 'CREATED',
    PUBLISHED = 'PUBLISHED',
    IN_PROCESSING = 'IN_PROCESSING',
    SUCCESS = 'SUCCESS',
    PENDING_APPROVAL = 'PENDING_APPROVAL',
    SUCCESS_TRANSACTION_FAILED = 'SUCCESS_TRANSACTION_FAILED',
    WAITING_REFUND = 'WAITING_REFUND',
    REFUNDED = 'REFUNDED',
    FAILED = 'FAILED',
    CANCELED = 'CANCELED',
}

export enum CardPaymentFailureReason {
    AUTHORIZATION_FAILED_3D_SECURE_MANDATORY = 'AUTHORIZATION_FAILED_3D_SECURE_MANDATORY',
    INVALID_CARD = 'INVALID_CARD',
    CVV2_FAILURE = 'CVV2_FAILURE',
    INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
    CARD_TYPE_NOT_SUPPORTED = 'CARD_TYPE_NOT_SUPPORTED',
    REFERENCED_TRANSACTION_FAILED_GENERAL_ERROR = 'REFERENCED_TRANSACTION_FAILED_GENERAL_ERROR',
    // NEW
    INCORRECT_CARD_NUMBER = 'INCORRECT_CARD_NUMBER',
    CARD_EXPIRED = 'CARD_EXPIRED',
    GENERAL_ERROR = 'GENERAL_ERROR',
}
export interface CardPaymentPrepareRequest {
    token?: string
    applicationId?: number
    transactionId?: number
    paymentType: CardPaymentType
    locale: string
    recurring?: boolean | null
}

export interface CardPaymentResolvedRequest {
    cardPaymentId: string
}
export enum VendorRequestType {
    WEB = 'WEB',
    CUSTOM = 'CUSTOM',
    VIRTUAL_CARD = 'VIRTUAL_CARD',
    SELF_CHECKOUT = 'SELF_CHECKOUT',
}

export interface CardPaymentStatusResponse {
    token: string
    status: CardPaymentStatus
    failureReason: CardPaymentFailureReason
    vendorSuccessUrl: string
    vendorErrorUrl: string
    vendorRequestType: VendorRequestType
    inStore: boolean
    verified: boolean
    vendorPackage: string
    applicationApprovalCheckType: ApplicationApprovalCheckType
    applicationApprovalId: number
    paymentType: CardPaymentType
}

export interface CardPaymentPrepareResponse {
    providerIdentifier: string
    cardPaymentId: number
    providerUrl: string
    provider: CardPaymentProvider
    cardPaymentRequestId: string
    amount: number
    providerMetadata?: any
}

export enum SignAndBuyStatus {
    TRANSACTION_CREATED = 'TRANSACTION_CREATED',
    PENDING_APPROVAL = 'PENDING_APPROVAL',
}
export enum CheckoutFlowType {
    REGULAR = 'REGULAR',
    FINANCIAL_DATA_UPDATE = 'FINANCIAL_DATA_UPDATE',
    PSD2_DATA_UPDATE = 'PSD2_DATA_UPDATE',
}

export interface VendorRequestInvalidateRequest {
    token: string
}

export enum FailedReasonType {
    FAIL_CREDIT_CHECK = 'FAIL_CREDIT_CHECK',
    NO_PAYMENT_OPTIONS = 'NO_PAYMENT_OPTIONS',
    EMPTY = 'EMPTY',
}

export enum IDDocumentType {
    PERSONAL_ID = 'PERSONAL_ID',
    PASSPORT = 'PASSPORT',
    DRIVING_LICENSE = 'DRIVING_LICENSE',
}

export interface AddressDetailsRequest {
    token: string
    postZipCodebookId: number
    settlementCodebookId: number
    streetCodebookId?: number
    street?: string
    houseNumber: string
    additionalAddressDetails?: string
}

export interface AddressDetailsResponse {
    postZipCodebookId?: number
    settlementCodebookId?: number
    streetCodebookId?: number
    street?: string
    houseNumber?: string
    floor?: string
    apartmentNumber?: string
    entrance?: string
    blockNumber?: string
}

export interface FinancialDetailsResponse {
    income: number | null
    bankAccountNumber: string | null
    employmentType: EmploymentType | null
    monthlyCreditExpense: number | null
    employmentContractType: EmploymentContractType | null
    numberOfDependants: number | null
    industryCodebookId: number | null
}

export enum IndustryOfEmployment {
    StateAdministration = 'State administration',
    Healthcare = 'Healthcare',
    Industry = 'Industry',
    BuildingIndustry = 'Building industry',
    FinanceInsurance = 'Finance, insurance',
    Law = 'Law',
    Tourism = 'Tourism',
    Agriculture = 'Agriculture',
    TradeServiceIndustry = 'Trade, service industry',
    Education = 'Education',
    Transport = 'Transport',
    PoliceNationalDefenseDisasterManagementNAV = 'Police, National Defense, Disaster Management, NAV',
    TelecommunicationsIT = 'Telecommunications, IT',
    ShippingTransportation = 'Shipping, transportation',
    Other = 'Other',
}

export enum MaritalStatus {
    SINGLE = 'SINGLE',
    MARRIED = 'MARRIED',
    WIDOWED = 'WIDOWED',
    DIVORCED = 'DIVORCED',
}

export interface MaritalStatusResponse {
    maritalStatus: MaritalStatus
}

export interface MaritalStatusRequest {
    token: string
    maritalStatus: MaritalStatus
}

export interface EmploymentInfoRequest {
    token: string
    income: number
    employmentType: EmploymentType
    employerName?: string
    companyTaxNumber?: string
    publicService?: boolean
}

export interface CancelTransactionRequest {
    surrogateId?: string | null
    token: string
    maturityPriceId: number | null
    maturityGraceId: number | null
    firstPaymentDate: number | null
}

export interface ValidationDataDTO {
    applicationId: number
    status: boolean
    validatedPaymentPlan: PaymentPlanDTO
    hasPossibleCashFlows: boolean
    paymentPlans: PaymentPlanDTO[]
    availableExposure: number
    recommendedPurchaseAmount: number
    testGroup: TestAB
}

export interface VendorPackageDTO {
    code: string
    amount: number
    description: string
}

export interface VendorPaymentEventProducerDTO {
    vendorPaymentId: number
    createdTime: string
    lastUpdateTime: string
}

export interface BackToSchoolAmountInfoDTO {
    amount: number
    description: string
}

export interface BackToSchoolDTO {
    isBackToSchoolEnabled: boolean
    amounts: BackToSchoolAmountInfoDTO[]
}

export interface BankDTO {
    id: number
    name: string
    description: string
}

export interface ProductDTO {
    id: number
    active: boolean
    name: string
    description: string
    code: string
    maximumInstallments: number
    createdTime: string
    maturities: MaturityDTO[]
    downPaymentActive: boolean
    adjustmentActive: boolean
    inStore: boolean
    verifiedCustomersRequired: boolean
}

export interface LoanAmountInfoDTO {
    minAmount: number
    maxAmount: number
}

export interface VendorRequestValidityPeriodDTO {
    period: number
    identifier: string
}

export interface VendorRequestInfoDTO {
    vendorPackages: VendorPackageDTO[]
    isBackToSchoolEnabled: boolean
    backToSchoolAmounts: BackToSchoolAmountInfoDTO[]
    inStoreEnabled: boolean
    products: ProductDTO[]
    defaultProduct: ProductDTO
    defaultProductInStore: ProductDTO
    loanAmountInfo: LoanAmountInfoDTO
    vendorPartialDeliveryEnabled: boolean
    validityPeriods: VendorRequestValidityPeriodDTO[]
    verifiedCustomersRequired: boolean
    vendorName: string
    ageInfo: {
        minAge: number
        maxAge: number
    }
}

export interface OffersSizeDTO {
    size: number
    page: number
}
export enum VendorRequestStatus {
    PENDING = 'PENDING',
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED',
    CANCELED = 'CANCELED',
    EXPIRED = 'EXPIRED',
}

export interface VendorRequest {
    id: number
    surrogateId: string
    vendorName: string
    vendorId: number
    validUntil: string
    amount: number
    url: string
    status: VendorRequestStatus
    vendorTransactionId: string
    transactionId: number
    vendorProductCode: string
    firstName: string
    lastName: string
    phoneNumber: string
    email: string
    address: string
    houseNumber: string
    zip: string
    city: string
    birthDate: string
    taxNumber: string
    language: string
    maximumInstallments: number
    backToSchool: boolean
    inStore: boolean
    createdTime: number
    vendorPackageDescription: string
    vendorPackageDescriptionWithPackage: string
    vendorPackageDescriptionWithPrice: string
    vendorUserName?: string
    vendorUserLastName?: string
    vendorUserPhoneNumber?: string
    vendorProductName: string
    vendorUserEmail?: string
}

export enum VendorRequestProgram {
    BackToSchool = 'backToSchool',
    NoProgram = 'noProgram',
}

export enum KYC_CHECK_RESULT_RESPONSE {
    PENDING = 'PENDING',
    VERIFIED = 'VERIFIED',
    NOT_VERIFIED = 'NOT_VERIFIED',
}

export interface ValidateVendorResponse {
    vendorLogoUrl: string
    vendorName: string
}

export interface AuthorizationCodeResponse {
    authorizationCode: string
    authorizationCodeImageBase64: string
    validUntil: any // Date TODO: fix type
    amount: number
}

export enum VendorRequestAuthorizationStatus {
    PENDING = 'PENDING',
    VENDOR_CONFIRMED = 'VENDOR_CONFIRMED',
    AUTHORIZED = 'AUTHORIZED',
    REJECTED = 'REJECTED',
}

export enum VendorRequestAuthorizationStatusCode {
    GLOBAL_MIN_REQUIREMENT = 'GLOBAL_MIN_REQUIREMENT',
    AMOUNT_EXCEEDS_INITIALLY_AMOUNT = 'AMOUNT_EXCEEDS_INITIALLY_AMOUNT',
    EXPIRED_GLOBAL = 'EXPIRED_GLOBAL',
    EXPIRED_SECURITY_PROMPT = 'EXPIRED_SECURITY_PROMPT',
    USER_REJECTED = 'USER_REJECTED',
}

export interface AuthorizationStatusResponse {
    status: VendorRequestAuthorizationStatus
    statusCode: VendorRequestAuthorizationStatusCode
    initialAmount: number
    authorizedAmount: number
}

export interface SecurityConfirmationRequest {
    token: string
    userConfirmation: boolean
}

export enum AuthorizationCodeError {
    AUTHORIZATION_CODE_IS_NOT_VALID = 'AUTHORIZATION_CODE_IS_NOT_VALID',
    PURCHASE_AMOUNT_IS_SHORT = 'PURCHASE_AMOUNT_IS_SHORT',
    CART_TOTAL_BELOW_MINIMUM = 'CART_TOTAL_BELOW_MINIMUM',
    SECURITY_PROMPT_EXPIRED = 'SECURITY_PROMPT_EXPIRED',
    SECURITY_PROMPT_WAS_NOT_CONFIRMED = 'SECURITY_PROMPT_WAS_NOT_CONFIRMED',
}

export enum CookieTypes {
    Necessary = 'necessary',
    Analytical = 'analytical',
    Functional = 'functional',
    Marketing = 'marketing',
}

export enum DataLayerEvent {
    PrequalifySuccess = 'pq_success',
}

export interface IdentityCardValidationRequest {
    vendorPhoneNumber: string
    identityCardType: IdentityCardType
    identityCardNumber: string
}

export interface ExistingUserVendorCustomRequest {
    vendorTransactionId: string
    phoneNumber: string
    vendorProductCode: string
    amount: number
    language: string
    validityPeriodIdentifier: string
    inStore: boolean
    customerIdentified?: boolean
}

export interface VendorCustomRequest {
    vendorTransactionId: string
    vendorProductCode: string
    amount: number
    vendorPhoneNumber: string
    vendorFirstName: string
    vendorLastName: string
    vendorEmail?: string
    vendorPostZipCodebookId?: number
    vendorSettlementCodebookId?: number
    vendorStreetCodebookId?: number
    vendorStreet?: string
    vendorHouseNumber?: string
    vendorBirthDate?: string
    vendorIdentityCardType?: IdentityCardType
    vendorIdentityCardNumber?: string
    vendorIdentityCardPlaceOfIssueCodebookId?: string | number
    vendorIdentityCardDateOfExpire?: string
    validityPeriodIdentifier: string
    inStore?: boolean
    customerIdentified?: boolean
    vendorTaxNumber?: string
    vendorIdentityCardPlaceOfIssue?: string
}

export interface VendorRemoteOfferRequest {
    vendorTransactionId: string
    vendorProductCode: string
    amount: number
    vendorPhoneNumber: string
    vendorFirstName: string
    vendorLastName: string
    email?: string
    vendorEmail?: string
    validityPeriodIdentifier: string
}

export interface VendorValidUntilToken {
    token?: string
    url?: string
    validUntil: number
    qrCodeBase64: string
}

export interface CancelVendorCustomRequest {
    id: number
}
export enum NotificationType {
    SMS = 'SMS',
    EMAIL = 'EMAIL',
}

export interface SendPreQualifyNotificationRequest {
    receiverIdentification: string //Phone number or email
    vendorId?: number
    notificationType: NotificationType
}

export interface TransactionRequestDTO {
    createdTimeFrom: string
    createdTimeTo: string
    searchCriteria?: string
    verificationStatus?: AccountVerificationStatus
    deliveryStatus?: TransactionDeliveryStatus | null
    vendorSettlementStatus?: TransactionVendorSettlementStatusDTO | null
    verificationCandidate?: boolean | null
}

export interface SendPreQualifySmsRequest {
    phoneNumber: string
}

export enum TransactionDeliveryStatus {
    UNDELIVERED = 'UNDELIVERED',
    DELIVERED = 'DELIVERED',
}

export enum TransactionVendorSettlementStatusDTO {
    UNPAID = 'UNPAID',
    PAID = 'PAID',
    DISPUTED = 'DISPUTED',
    FAILED = 'FAILED',
}

export enum VerificationDocumentStatus {
    CREATED = 'CREATED',
    IN_REVIEW = 'IN_REVIEW',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CLOSED = 'CLOSED',
}

export enum VendorVerificationState {
    CANDIDATE = 'CANDIDATE',
    NOT_CANDIDATE = 'NOT_CANDIDATE',
    VERIFIED = 'VERIFIED',
    IN_REVIEW = 'IN_REVIEW',
}

export enum TransactionClientSettlementStatus {
    PRE_APPROVED = 'PRE_APPROVED',
    UNPAID = 'UNPAID',
    PAID = 'PAID',
    DISPUTED_CANCEL_CREDIT = 'DISPUTED_CANCEL_CREDIT',
    DISPUTED_ADVANCED_PAYMENT = 'DISPUTED_ADVANCED_PAYMENT',
    DELAYED = 'DELAYED',
    NPL = 'NPL',
    CONTRACT_CANCELLED = 'CONTRACT_CANCELLED',
    CONTRACT_TRANSFERRED = 'CONTRACT_TRANSFERRED',
    EXECUTED = 'EXECUTED',
    CANCELLED = 'CANCELLED',
}

export enum TransactionFinancialStatus {
    PENDING = 'PENDING',
    PLEDGE_IN_PROGRESS = 'PLEDGE_IN_PROGRESS',
    PLEDGED = 'PLEDGED',
    TRANSFER_IN_PROGRESS = 'TRANSFER_IN_PROGRESS',
    TRANSFERRED = 'TRANSFERRED',
}

export interface ResultPageDTO<T> {
    result: T[]
    totalElements: number
    totalPages: number
}

export enum DisputeStatus {
    NEW = 'NEW',
    IN_REVIEW = 'IN_REVIEW',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CLOSED = 'CLOSED',
}

export interface TransactionVendorDTO {
    id: number
    surrogateId: string
    vendorTransactionId: string
    vendorProductCode: string
    createdTime: string
    amount: number
    vendorProductName: string
    numberOfMonths: number
    personId: number
    phoneNumber: string
    firstName: string
    lastName: string
    accountVerificationStatus: AccountVerificationStatus
    deliveryCandidate: boolean
    verificationDocumentStatus: VerificationDocumentStatus
    verificationOwner: boolean
    verificationState?: VendorVerificationState
    transactionDeliveryStatus: TransactionDeliveryStatus
    transactionClientSettlementStatus: TransactionClientSettlementStatus
    transactionVendorSettlementStatus: TransactionVendorSettlementStatusDTO
    transactionFinancialStatus: TransactionFinancialStatus
    disputable: boolean
    disputeStatus: DisputeStatus
    salespersonId: number
}

export enum VendorPaymentInvoiceFileType {
    PDF = 'PDF',
    XLSX = 'XLSX',
    CSV_DELIMITER_COMMA = 'CSV_DELIMITER_COMMA',
    CSV_DELIMITER_SEMICOLON = 'CSV_DELIMITER_SEMICOLON',
}

export interface VendorPaymentInvoiceFileResponse {
    correlationId: string
    fileName: string
    contentType: string
    fileType: VendorPaymentInvoiceFileType
    fileUrl: string
}

export interface VendorPaymentInvoiceResponse {
    invoiceId: number
    invoiceNumber: string
    invoiceDate: string
    payment: number
    fees: number
    status: string
    vendorPaymentInvoiceFileList: VendorPaymentInvoiceFileResponse[]
}

export interface VendorPaymentInvoiceSearchRequest {
    searchCriteria: string
    createdFrom: string
    createdTo: string
    status: string | null
}

// used for table filtering
export enum PaymentAndInvoiceStatus {
    PAID = 'PAID',
    UNPAID = 'UNPAID',
    ALL = 'ALL',
}

export interface VendorAgentResponseDTO {
    id: number
    agentSignature: string
    phoneNumber: string
    status: VendorAccountStatus
    version: number
}

export interface VendorAgentRequestDTO {
    agentSignature: string
    phoneNumber: string
    status: VendorAccountStatus
    version?: number
}

export interface PaymentPlanRequestDTO {
    vendorProductCode: string
    amount: number
    maturityType: MaturityType
}

export enum StateMachineStateType {
    KYC = 'KYC',
    WATCHLIST = 'WATCHLIST',
    VALIDATE_OFFER = 'VALIDATE_OFFER',
    CREDIT_BUREAU_CHECK = 'CREDIT_BUREAU_CHECK',
    FINANCIAL_DATA_CONFIRMATION = 'FINANCIAL_DATA_CONFIRMATION',
    DECISION = 'DECISION',
}

export enum StateMachineStatus {
    PENDING = 'PENDING',
    ACCEPTED = 'ACCEPTED',
    REJECTED = 'REJECTED',
}

export interface StateLog {
    state: StateMachineStateType
    status: StateMachineStatus
}

export interface StateMachineDto {
    entityId: string
    states: StateLog[] | null
    currentState?: CurrentState
    executedStates?: ExecutedState[]
}

export enum TriggerType {
    MANUAL = 'MANUAL',
    AUTO_EXECUTABLE = 'AUTO_EXECUTABLE',
    BACKEND_PROCESSING = 'BACKEND_PROCESSING',
    CONFIRMATION = 'CONFIRMATION',
}

export interface CurrentState {
    state: string
    triggerType: TriggerType
    message?: string
    errorCode?: string
}

export interface ExecutedState {
    state: string
    triggerType: TriggerType
}

export interface ConsentV2Request {
    token: string
    termsAndConditionsConsent: boolean
    creditBureauConsent: boolean
    marketingConsent?: boolean
}

export interface OfferPlanDataRequest {
    amount: number
    maturityPriceId: number
    maturityGraceId: number
    firstPaymentDate: string // "ddMMyyyy" (no separators, e.g. "15042026") — confirmed by backend
    insuranceIncluded: boolean
}

// --- Credit (GPL) Purchase Limit ---
export interface CreditPurchaseRangeResponse {
    minPurchaseAmount: number
    maxPurchaseAmount: number
}

// --- Credit (GPL) Payment Plan Calculation ---
export interface CreditPaymentPlanRequest {
    token: string
    amount: number
    insuranceEnabled: boolean
    firstPaymentDate?: number | null
}

export interface CreditPaymentDataResponse {
    token: string
    purchaseAmount: number
    paymentCycleDates: number[]
    paymentPlans: CreditPaymentPlanDTO[]
    insuranceRate?: number
    rbpEnabled?: boolean
}

export interface CreditPaymentPlanDTO {
    possible: boolean
    termMaturity: MaturityDTO
    graceMaturity: MaturityDTO | null
    loanAmount: number
    totalAmount: number
    installmentAmount: number
    firstInstallmentAmount: number
    firstInstallmentDate: number
    numberOfInstallments: number
    nominalInterestRate: number
    costOfReview: number
    costOfRisk: number
    clientInterest: number
    interestAndFees: number
    eom: number
    downPaymentTotal: number
    downPaymentPrincipal: number
    calculationMethod: CostsCalculationMethod
    totalAmountWithoutDiscount: number | null
    clientInterestWithoutDiscount: number | null
    eomWithoutDiscount: number | null
    nominalInterestRateWithoutDiscount: number | null
    costOfReviewWithoutDiscount: number | null
    costOfRiskWithoutDiscount: number | null
    interestAndFeesWithoutDiscount: number | null
    totalLoanAmount: number
    insurancePremiumFee: number //TODO - DELETE THIS LSPASIC
    requiresCreditReviewFeeConsent: boolean
}

export interface CompaniesListDTO {
    companyTaxNumber: string
    companyName: string
}

export interface CreditTokenValidationResponse {
    token: string
}

export interface EligibilityValidationResult {
    valid: boolean
    reason: string
    message: string | null
}

export interface EligibilityValidationResponse {
    validationResults: EligibilityValidationResult[]
}

export enum CheckoutRequestStatus {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    ADDITIONAL_REVIEW = 'ADDITIONAL_REVIEW',
}

export interface CheckoutStateMachineStatusDto {
    checkoutResultProvided: boolean
    checkoutRequestStatus: CheckoutRequestStatus | null
    checkoutRequestType: string
    additionalInfo: {
        applicationApprovalCheckType: string | null
        stateMachineState: string
        applicationApprovalStatus: string | null
    }
}

export interface PurchaseAmountDTO {
    minPurchaseAmount: number
    maxPurchaseAmount: number
    purchaseAllowed: boolean
    cpiPurchaseAllowed: boolean
    maxPurchaseAmountCpiIncluded: number
}

export interface UpsellCheckoutInitRequest {
    token: string
}

export interface UpsellCheckoutInitResponse {
    token: string
}

export interface GplCheckoutInitResponse {
    token: string
}

export enum UpsellOfferState {
    NOT_APPLICABLE = 'NOT_APPLICABLE',
    PENDING = 'PENDING',
    READY = 'READY',
    NONE = 'NONE',
}

export interface UpsellOfferStateDto {
    state: UpsellOfferState
    token: string | null
    offerUrl: string | null
    validUntil: number | null // Unix epoch seconds
    offeredAmount: number | null
}

export enum UpsellResponseKey {
    UPSELL_OFFER_NO_LONGER_AVAILABLE = 'UPSELL_OFFER_NO_LONGER_AVAILABLE',
    UPSELL_OFFER_EXPIRED = 'UPSELL_OFFER_EXPIRED',
    UPSELL_NOT_ELIGIBLE = 'UPSELL_NOT_ELIGIBLE',
}

// Client-side personal details shape (formerly the PersonalDetails model class).
// Consumers build this object directly instead of instantiating a class.
export interface PersonalDetailsClientDTO {
    firstName: string
    lastName: string
    birthFirstName?: string
    birthLastName?: string
    mothersMaidenName?: string
    identityCardType?: string
    identityCardNumber?: string
    email: string
    postZipCodebookId: number
    settlementCodebookId: number
    streetCodebookId?: number
    street?: string
    houseNumber: string | number
    bankAccountNumber?: string
    countryOfBirthCodebookId: number
    placeOfBirth: string
    dateOfBirth: string
    gender: string
    taxNumber: string
    terms: boolean
    citizenship?: boolean
    marketingConsent: boolean
    address?: string
    additionalAddress?: string
    creditBureauConsent?: boolean
}

// Client-side financial details shape (formerly the FinancialDetails model class).
export interface FinancialDetailsClientDTO {
    financialProductBankAccountLimit?: boolean
    financialProductCreditCard?: boolean
    financialProductBankCredit?: boolean
    financialProductLeasing?: boolean
    income: number
    monthlyCreditExpense?: number
    monthlyHouseCredit?: number
    monthlyCashOrMortgageCredit?: number
    monthlyLeasingOrAutoCredit?: number
    employerCredit?: boolean
    useCreditCardForInstallmentPayment?: boolean
    creditCardLimit?: number
    executionInLastYear: boolean
    executionInLastYearCreditOrLeasing?: boolean
    executionInLastYearAmount?: ExecutionAmountDTO
    personalBankruptcyInProgress: boolean
    lateInstallmentPayment?: boolean
    lateInstallmentPaymentTime?: LatePaymentTime
    employmentType: EmploymentType
    employmentContractType?: EmploymentContractType
    bankAccountNumber?: string
    numberOfChildren?: number
    numberOfConsecutivePaymentMonths?: string
    numberOfRecentLoans?: string
    numberOfDependants?: number
    userIdentityNumber?: string
    userId?: string
    status?: string
    requestId?: string
}

// Vendor mock product item shape (formerly the VendorMockProductItem model class).
export interface VendorMockProductItemDTO {
    name: string
    description: string
    productImage: string
    vendorApiKey: string
    vendorTransactionId: string
    vendorProductCode: string
    amount: string
    successUrl: string
    errorUrl: string
    vendorFirstName: string
    vendorLastName: string
    vendorEmail: string
    language: string
    cartItems: unknown[]
}
